import api from "@/api/v1-jwt/config/api";
import {
  useQuery,
  QueryFunctionContext,
  UseQueryOptions,
} from "@tanstack/react-query";
import {
  bulkInvoiceUploadEntitiesSchema,
  BulkInvoiceUploadEntities,
} from "../types";
import { invoiceQueryKeys } from "../query-keys";
import { createErrorHandler } from "@/utils/errorHandler";

type BulkUploadsListQueryKey = ReturnType<
  typeof invoiceQueryKeys.bulkUploads.list
>;

const fetchBulkInvoiceUploads = async ({
  queryKey,
}: QueryFunctionContext<BulkUploadsListQueryKey>): Promise<BulkInvoiceUploadEntities> => {
  const [, , , userId] = queryKey;
  const url = `/platform/invoices/${userId}/bulk`;
  const response = await api.get(url);
  return bulkInvoiceUploadEntitiesSchema.parse(response.data);
};

export const bulkUploadList = {
  createOptions: <TSelected = BulkInvoiceUploadEntities>(
    userId: string,
    options?: Omit<
      UseQueryOptions<
        BulkInvoiceUploadEntities,
        Error,
        TSelected,
        BulkUploadsListQueryKey
      >,
      "queryKey" | "queryFn"
    >
  ) => {
    const defaultOptions: UseQueryOptions<
      BulkInvoiceUploadEntities,
      Error,
      TSelected,
      BulkUploadsListQueryKey
    > = {
      queryKey: invoiceQueryKeys.bulkUploads.list(userId),
      queryFn: fetchBulkInvoiceUploads,
      staleTime: 5 * 60 * 1000,
      cacheTime: 10 * 60 * 1000,
      onError: createErrorHandler(),
    };
    return { ...defaultOptions, ...options };
  },
  useQuery: <TSelected = BulkInvoiceUploadEntities>(
    userId: string,
    options?: Omit<
      UseQueryOptions<
        BulkInvoiceUploadEntities,
        Error,
        TSelected,
        BulkUploadsListQueryKey
      >,
      "queryKey" | "queryFn"
    >
  ) => {
    const queryOptions = bulkUploadList.createOptions<TSelected>(
      userId,
      options
    );
    return useQuery<
      BulkInvoiceUploadEntities,
      Error,
      TSelected,
      BulkUploadsListQueryKey
    >({
      ...queryOptions,
      enabled: options?.enabled ?? !!userId,
    });
  },
};
